import { BACKEND_URL } from "../config/brand";

const TOKEN_KEY = "digirobot_admin_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export async function login(email, password) {
  const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Identifiants invalides");
  }

  const data = await response.json();
  setToken(data.access_token);
  return data;
}

export async function fetchMe() {
  const response = await authFetch("/api/auth/me");
  if (!response.ok) {
    // 401 déjà purgé par authFetch ; 403 = token/rôle invalide
    if (response.status === 403) clearToken();
    const err = new Error("Session expirée");
    err.status = response.status;
    err.authFailed = response.status === 401 || response.status === 403;
    throw err;
  }
  return response.json();
}

export async function authUpload(path, file, fields = {}) {
  const token = getToken();
  const body = new FormData();
  body.append("file", file);
  for (const [key, value] of Object.entries(fields)) {
    if (value != null) body.append(key, value);
  }
  const response = await fetch(`${BACKEND_URL}${path}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  });
  if (response.status === 401) {
    clearToken();
    if (window.location.pathname.startsWith("/admin") && !window.location.pathname.includes("/login")) {
      window.location.href = "/admin/login";
    }
  }
  return response;
}

export async function authFetch(path, options = {}) {
  const token = getToken();
  const headers = {
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    clearToken();
    if (window.location.pathname.startsWith("/admin") && !window.location.pathname.includes("/login")) {
      window.location.href = "/admin/login";
    }
  }

  return response;
}

export function logout() {
  clearToken();
  window.location.href = "/admin/login";
}

export async function submitContact({ name, email, phone, subject, message }) {
  const body = {
    name,
    email,
    phone: phone || undefined,
    subject: subject || undefined,
    message: subject ? `[${subject}] ${message}` : message,
  };

  const response = await fetch(`${BACKEND_URL}/api/contact`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Envoi impossible");
  }

  return response.json();
}

export async function publicFetch(path, options = {}) {
  if (!BACKEND_URL) {
    throw new Error("Backend URL not configured");
  }
  return fetch(`${BACKEND_URL}${path}`, options);
}

/**
 * Contenu éditable du site (GET /api/content).
 * Réponse : { texts: { [key]: string }, items?: ... } — overrides non vides.
 */
export async function fetchContent() {
  const response = await publicFetch("/api/content");
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Impossible de charger le contenu");
  }
  return response.json();
}

/**
 * Enregistre un ou plusieurs textes (PUT /api/admin/content).
 * Body : { texts: { key: value } }
 * @param {Record<string, string>} texts
 */
export async function saveContentTexts(texts) {
  const response = await authFetch("/api/admin/content", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ texts }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Erreur lors de l'enregistrement");
  }
  return response.json();
}

/** Enregistre une seule clé (PUT /api/admin/content/:key). */
export async function saveContentText(key, value) {
  const response = await authFetch(`/api/admin/content/${encodeURIComponent(key)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ value, type: "text" }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || "Erreur lors de l'enregistrement");
  }
  return response.json();
}
